import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { captureMessage } from "@sentry/react";
import { v4 as uuidv4 } from 'uuid';
import { useToast, Tabs } from '@aidkitorg/component-library';
import { usePost } from '../../../API';
import { useLocalizedStrings } from '../../../Localization';
import { QueueCard } from '../../Reviews/components/QueueCard';
import { QueueApplicantsTable, QueueApplicantRow } from '../../Reviews/components/QueueApplicantsTable';
import { openWindowWithLoadingContent } from '../../../LoadingTabUtil';
import { useDashboards } from '../../../Contexts/DashboardsContext';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import type * as survey from '@aidkitorg/types/lib/survey';
import type { FilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import type { BooleanExpr } from '@aidkitorg/types/lib/survey';
import { nameForColumn } from '@aidkitorg/types/lib/translation/expr_to_sql';

function withAdditionalColumns(rows: any[], columns?: survey.ValueExpr[]): QueueApplicantRow[] {
  const keys = (columns ?? []).map(nameForColumn).filter((k): k is string => !!k);
  if (keys.length === 0) return rows as QueueApplicantRow[];
  return rows.map((row) => ({ ...row, additionalColumns: Object.fromEntries(keys.map((key) => [key, row[key]])) }));
}

interface QueueComponentRendererProps {
  queue: survey.Queue;
  componentId: string;
  dashboardId?: string;
  viewFilters?: FilterConfig;
  dashboardFilter?: BooleanExpr;
  /**
   * Filter-aware dashboard refresh from the parent page.
   */
  onRefreshDashboardData?: () => Promise<void> | void;
}

export const QueueComponentRenderer: React.FC<QueueComponentRendererProps> = ({ queue, componentId, dashboardId, viewFilters, dashboardFilter, onRefreshDashboardData }) => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const { toast } = useToast();
  // suppressErrorDisplay: the API layer would otherwise toast the raw error
  // code; we show a single user-friendly toast in the claim handlers instead.
  const takeApplicantPost = usePost('/queues/take_applicant', { noRetry: true, suppressErrorDisplay: true });
  const { loadDashboardComponentData, fetchDashboardById, dashboardsById } = useDashboards();
  const forceRefreshKey = dashboardId ? dashboardsById[dashboardId]?.forceRefreshKey : undefined;
  // Re-load when the dashboard config itself changes. A filter refetch swaps in new
  // dashboardRefs (the filter is baked into the ref), so without depending on lastUpdated
  // the queue keeps loading whatever ref it captured earlier and its count lags the applied
  // filter. DashboardComponentCard already depends on lastUpdated for the same reason.
  const lastUpdated = dashboardId ? dashboardsById[dashboardId]?.lastUpdated : undefined;
  const prevForceRefreshKeyRef = useRef(forceRefreshKey);

  const [inQueue, setInQueue] = useState<number | undefined>(undefined);
  const [inReview, setInReview] = useState<number | undefined>(undefined);
  const [completed, setCompleted] = useState<number | undefined>(undefined);
  const [inQueueRows, setInQueueRows] = useState<QueueApplicantRow[]>([]);
  const [assignedRows, setAssignedRows] = useState<QueueApplicantRow[]>([]);
  const [completedRows, setCompletedRows] = useState<QueueApplicantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const showApplicantsTable = !!queue.showApplicantsTable;

  // Clear both the row arrays and the count badges so a failed load can't leave
  // stale totals on the card while the table is empty/outdated.
  const resetData = () => {
    setInQueue(undefined);
    setInReview(undefined);
    setCompleted(undefined);
    setInQueueRows([]);
    setAssignedRows([]);
    setCompletedRows([]);
  };

  useAsyncEffect(async (signal) => {
    // Force-refresh (manual Refresh control) or a claim-triggered reload
    // (refreshKey bump) must bypass the dashboards-context cache so counts and
    // rows reflect the latest data instead of a stale (up to 10 min) payload.
    const isForceRefresh = prevForceRefreshKeyRef.current !== undefined && forceRefreshKey !== prevForceRefreshKeyRef.current;
    prevForceRefreshKeyRef.current = forceRefreshKey;
    const skipCache = isForceRefresh || refreshKey > 0;

    setLoading(true);
    // Clear any prior error up front so an in-flight retry shows the loading
    // state instead of the stale load-failure EmptyState.
    setLoadError(false);
    try {
      const result = await loadDashboardComponentData(
        queue as any,
        viewFilters,
        dashboardFilter,
        { componentId, dashboardId, ...(skipCache ? { skipCache: true } : {}) }
      );
      // Drop this response if a newer load has superseded it (deps changed — e.g. a filter
      // refetch swapped in new dashboardRefs). loadDashboardComponentData resolves its ref
      // from dashboardsById at call time and can't be cancelled, so without this a slower
      // stale (unfiltered) load could resolve last and overwrite the filtered count.
      if (signal.aborted) return;
      if (result?.success && result.data) {
        setInQueue(result.data.rowCount ?? 0);
        const refsData = result.data.data as Record<string, any> | undefined;
        setInQueueRows(withAdditionalColumns(refsData?.in_queue?.rows ?? [], queue.columns));
        if (refsData?.assigned) {
          setInReview(refsData.assigned.rowCount ?? 0);
          setAssignedRows(withAdditionalColumns(refsData.assigned.rows ?? [], queue.columns));
        }
        if (refsData?.completed) {
          setCompleted(refsData.completed.rowCount ?? 0);
          setCompletedRows(withAdditionalColumns(refsData.completed.rows ?? [], queue.columns));
        }
      } else {
        // Load failed or returned success: false — clear stale rows and counts
        // so we don't keep showing data from a prior successful fetch.
        setLoadError(true);
        resetData();
      }
    } catch (e) {
      if (signal.aborted) return;
      console.error('[QueueComponentRenderer] Error loading queue stats:', e);
      setLoadError(true);
      resetData();
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [componentId, dashboardId, refreshKey, forceRefreshKey, viewFilters, dashboardFilter, lastUpdated]);

  const startSectionPath = (() => {
    if (typeof queue.startSection === 'string' && queue.startSection) {
      return queue.startSection.startsWith('/') ? queue.startSection.slice(1) : queue.startSection;
    }
    if (queue.startSection && typeof queue.startSection === 'object') {
      return queue.startSection.path;
    }
    return 'review';
  })();

  const queueTitle = typeof queue.title === 'string'
    ? queue.title
    : queue.title?.en || 'Queue';

  const refreshQueueData = async () => {
    if (!dashboardId) return;
    // Prefer the parent's filter-aware refresh so the re-fetched dashboardRefs
    // still reflect the active view/section filters. Fall back to an unfiltered
    // refetch only when this queue is rendered outside the dashboard page.
    if (onRefreshDashboardData) {
      await onRefreshDashboardData();
    } else {
      await fetchDashboardById({ id: dashboardId, forceRefresh: true });
    }
    setRefreshKey(k => k + 1);
  };

  // Errors that mean the displayed counts/rows are out of date (applicant gone
  // or queue drained), so a refresh would show the user current state.
  const STALE_DATA_CLAIM_ERRORS = ['already_claimed', 'not_in_queue', 'no_applicants_in_queue'];

  const claimErrorMessage = (error?: string) => {
    switch (error) {
      case 'already_claimed': return L.dashboard.claim_error_already_claimed;
      case 'not_in_queue': return L.dashboard.claim_error_not_in_queue;
      case 'no_applicants_in_queue': return L.dashboard.claim_error_no_applicants_in_queue;
      case 'already_pulling_from_queue': return L.dashboard.claim_error_already_pulling_from_queue;
      case 'permission_denied_queue': return L.dashboard.claim_error_permission_denied_queue;
      case 'no user': return L.dashboard.claim_error_no_user;
      default: return L.dashboard.claim_applicant_error;
    }
  };

  const showClaimError = (error?: string) => {
    toast({ description: claimErrorMessage(error), variant: 'error' });
  };

  const handleClaimApplicant = async () => {
    if (window.confirm(L.dashboard.claim_applicant_confirmation)) {
      const randomId = uuidv4();
      const captureLog = (message: string) => captureMessage(`Queue request ${randomId} (admin panel): ${message}`);
      captureLog('confirm callback clicked');

      const win = openWindowWithLoadingContent();

      try {
        captureLog('making request');
        const resp = await takeApplicantPost({ queue: queue, dashboardId }, undefined, () => {
          captureLog('calling API');
        });
        captureLog(`got response ${JSON.stringify(resp)}`);

        if (resp && 'applicant' in resp) {
          const loc = `/admin-panel/a/${resp.applicant}/${startSectionPath}`;

          if (win) {
            win.location.href = loc;
          } else {
            history.push(loc);
          }

          toast({
            description: L.dashboard.applicant_claimed,
            variant: 'success'
          });

          await refreshQueueData();
        } else {
          win?.close();
          if (resp && STALE_DATA_CLAIM_ERRORS.includes(resp.error)) {
            await refreshQueueData();
          }
          showClaimError(resp?.error);
        }
      } catch (e) {
        console.warn("Error claiming applicant: ", e);
        toast({
          description: L.dashboard.claim_applicant_error,
          variant: 'error'
        });
        win?.close();
      }
    }
  };

  const handleClaimSpecific = async (uid: string) => {
    if (!window.confirm(L.dashboard.claim_applicant_confirmation)) return;

    const win = openWindowWithLoadingContent();
    try {
      const resp = await takeApplicantPost({ queue, uid, dashboardId });

      if (resp && 'applicant' in resp) {
        const loc = `/admin-panel/a/${resp.applicant}/${startSectionPath}`;

        if (win) {
          win.location.href = loc;
        } else {
          history.push(loc);
        }

        toast({ description: L.dashboard.applicant_claimed, variant: 'success' });

        await refreshQueueData();
      } else {
        win?.close();
        if (resp && STALE_DATA_CLAIM_ERRORS.includes(resp.error)) {
          await refreshQueueData();
        }
        showClaimError(resp?.error);
      }
    } catch (e) {
      console.warn('Error claiming applicant: ', e);
      toast({ description: L.dashboard.claim_applicant_error, variant: 'error' });
      win?.close();
    }
  };

  const hasSubmitCompleteField = !!queue.submitCompleteField;

  const applicantTabs = [
    {
      name: L.admin_panel.reviews.in_queue,
      key: 'in_queue',
      count: { value: inQueue ?? 0 },
      content: (
        <div className="mt-3 flex flex-col flex-1 min-h-0">
          <QueueApplicantsTable rows={inQueueRows} loading={loading} startSection={startSectionPath} onClaim={handleClaimSpecific} additionalColumns={queue.columns} />
        </div>
      ),
    },
    ...(hasSubmitCompleteField
      ? [
        {
          name: L.admin_panel.reviews.in_review,
          key: 'assigned',
          count: { value: inReview ?? 0 },
          content: (
            <div className="mt-3 flex flex-col flex-1 min-h-0">
              <QueueApplicantsTable rows={assignedRows} loading={loading} startSection={startSectionPath} showAssignment additionalColumns={queue.columns} />
            </div>
          ),
        },
        {
          name: L.admin_panel.reviews.completed,
          key: 'completed',
          count: { value: completed ?? 0 },
          content: (
            <div className="mt-3 flex flex-col flex-1 min-h-0">
              <QueueApplicantsTable rows={completedRows} loading={loading} startSection={startSectionPath} showAssignment additionalColumns={queue.columns} />
            </div>
          ),
        },
      ]
      : []),
  ];

  return (
    <QueueCard
      name={queueTitle}
      description={typeof queue.description === 'string' ? queue.description : queue.description?.en || ''}
      inQueue={inQueue ?? 0}
      inReview={inReview}
      completed={completed}
      onClaimApplicant={handleClaimApplicant}
      isLoading={loading}
      loadError={loadError}
      hasSubmitCompleteField={hasSubmitCompleteField}
    >
      {showApplicantsTable && (
        <Tabs justify="left" defaultTab="in_queue" tabs={applicantTabs} fillHeight />
      )}
    </QueueCard>
  );
};
